import type { ListInfo } from './productListsHelpers';

export type ModalFlowState =
  | { step: 'idle' }
  | {
      step: 'create-list';
      productId: string;
      productName: string;
      intent: 'add' | 'remove';
      allowDefaultPromotion?: boolean;
      isSubmitting: boolean;
    }
  | {
      step: 'change-list';
      productId: string;
      productName: string;
      initialSelectedListIds: string[];
      fromCreateFlow?: boolean;
      createdList?: ListInfo;
      intent: 'add' | 'remove';
      allowDefaultPromotion?: boolean;
      isSubmitting: boolean;
    };

export type ModalFlowAction =
  | {
      type: 'OPEN_CREATE_MODAL';
      productId: string;
      productName: string;
      intent: 'add' | 'remove';
      allowDefaultPromotion?: boolean;
    }
  | {
      type: 'OPEN_CHANGE_MODAL';
      productId: string;
      productName: string;
      initialSelectedListIds: string[];
      intent: 'add' | 'remove';
      allowDefaultPromotion?: boolean;
    }
  | { type: 'MODAL_DISMISSED' }
  | { type: 'SET_SUBMITTING'; isSubmitting: boolean }
  | {
      type: 'CREATE_LIST_SUCCESS';
      productId: string;
      productName: string;
      newListId: string;
      newListName: string;
      newListSlug: string;
      initialSelectedListIds: string[];
      intent: 'add' | 'remove';
      allowDefaultPromotion?: boolean;
    }
  | { type: 'CHANGE_LIST_DISMISS' }
  | { type: 'CHANGE_LIST_SUCCESS' }
  | { type: 'NEW_LIST_FOR_PRODUCT_REQUESTED' };

export const INITIAL_MODAL_FLOW: ModalFlowState = { step: 'idle' };

export function modalFlowReducer(
  state: ModalFlowState,
  action: ModalFlowAction
): ModalFlowState {
  if (process.env.NODE_ENV === 'development') {
    console.log('[ProductLists:modalFlow]', action.type, action);
  }

  switch (action.type) {
    case 'OPEN_CREATE_MODAL':
      return {
        step: 'create-list',
        productId: action.productId,
        productName: action.productName,
        intent: action.intent,
        allowDefaultPromotion: action.allowDefaultPromotion,
        isSubmitting: false,
      };

    case 'OPEN_CHANGE_MODAL':
      return {
        step: 'change-list',
        productId: action.productId,
        productName: action.productName,
        initialSelectedListIds: action.initialSelectedListIds,
        intent: action.intent,
        allowDefaultPromotion: action.allowDefaultPromotion,
        isSubmitting: false,
      };

    case 'MODAL_DISMISSED':
      return { step: 'idle' };

    case 'SET_SUBMITTING': {
      if (state.step === 'idle') return state;
      return { ...state, isSubmitting: action.isSubmitting };
    }

    case 'CREATE_LIST_SUCCESS':
      return {
        step: 'change-list',
        productId: action.productId,
        productName: action.productName,
        initialSelectedListIds: action.initialSelectedListIds,
        fromCreateFlow: true,
        createdList: {
          id: action.newListId,
          name: action.newListName,
          slug: action.newListSlug,
        },
        intent: action.intent,
        allowDefaultPromotion: action.allowDefaultPromotion,
        isSubmitting: false,
      };

    case 'CHANGE_LIST_DISMISS':
      return { step: 'idle' };

    case 'CHANGE_LIST_SUCCESS':
      return { step: 'idle' };

    case 'NEW_LIST_FOR_PRODUCT_REQUESTED': {
      if (state.step !== 'change-list') return state;
      const { productId, productName, intent, allowDefaultPromotion } = state;
      return {
        step: 'create-list',
        productId,
        productName,
        intent,
        allowDefaultPromotion,
        isSubmitting: false,
      };
    }

    default:
      return state;
  }
}
